import { TextField } from '@mui/material';
import { getInputDisplay, getMetricSpec } from '@strength/domain-workout';
import { useMetrics } from '../../../../../hooks/useMetrics';
import { DeltaIndicator } from './DeltaIndicator';
import { IncrementIconButton } from './IncrementIconButton';
import { BaseMetricInputProps } from './types';

const RANGE_REGEX = /\d+-\d+/;

/**
 * Allows incrementing/decrementing a set metric by a fixed amount.
 * For numeric metrics without units.
 */
export const ActualsIncrementInput = ({
  workoutSetId,
  setMetric,
  exerciseMetric,
  originalSetMetric,
  onEditSetMetric,
  increment = 1,
  min = undefined,
  max = undefined,
  disabled,
}: {
  increment?: number;
  min?: number; // minimum value
  max?: number; // maximum value
  disabled?: boolean;
} & BaseMetricInputProps) => {
  const { getMetric } = useMetrics();
  const metric = getMetric(exerciseMetric.metricId);
  const { value, placeholder } = getInputDisplay({
    metric,
    prescribedValue: originalSetMetric?.input,
    actualValue: setMetric?.input,
  });

  let startingValue = value;
  if (!startingValue) {
    const spec = getMetricSpec(metric);
    const parsed = spec.schema.safeParse(setMetric?.input);
    if (parsed.success && parsed.data?.type === 'range') {
      startingValue = parsed.data.min;
    }
  }

  return (
    <div className="flex flex-row gap-1">
      <IncrementIconButton
        disabled={(min !== undefined && Number(value) <= min) || disabled}
        onClick={() =>
          onEditSetMetric(workoutSetId, {
            metricId: exerciseMetric.metricId,
            input: getNewValue(startingValue.toString(), -increment),
          })
        }
        isIncrement={false}
      />
      <TextField
        disabled={disabled}
        value={String(value)}
        placeholder={String(placeholder)}
        InputProps={{
          sx: {
            mt: -3,
            width: '100%',
            '& input': {
              minWidth: `${String(value).length}ch`,
            },
          },
          endAdornment: (
            <DeltaIndicator
              metric={metric}
              prescribed={originalSetMetric?.input}
              actual={setMetric.input}
            />
          ),
          inputMode: 'numeric',
        }}
        onChange={(e) => {
          let newValue = parseInt(e.target.value) || 0;
          if (min !== undefined && newValue < min) {
            // if a value under the min is manually entered, use the min
            newValue = min;
          }
          if (max !== undefined && newValue > max) {
            // if a value over the max is manually entered, use the max
            newValue = max;
          }
          onEditSetMetric(workoutSetId, {
            metricId: exerciseMetric.metricId,
            input: String(newValue),
          });
        }}
      />
      <IncrementIconButton
        disabled={(max !== undefined && Number(value) >= max) || disabled}
        onClick={() =>
          onEditSetMetric(workoutSetId, {
            metricId: exerciseMetric.metricId,
            input: getNewValue(startingValue.toString(), increment),
          })
        }
      />
    </div>
  );
};

const getNewValue = (oldInput: string, increment: number) => {
  // If the old input was a keyword or a range, reset to 1
  if (RANGE_REGEX.test(oldInput)) {
    return '1';
  }

  return String(Number(oldInput) + increment);
};
